/**
 * The Military action (rulebook p.6): one of
 *   deploy  — a token from the supply becomes a legionnaire in the camp
 *   move    — the leader moves to an adjacent province and seizes its forum tile
 *   occupy  — a legionnaire leaves the camp for the leader's province and scores it
 */
import { addVp, afterAction, fail, playerOf, succeed, updatePlayer, } from '../internal.js';
import { MANIFESTS } from '../manifests.js';
/** Provinces the leader may move to from where it stands (R-013 for the graph). */
export function leaderDestinations(leader) {
    if (leader === 'camp')
        return [...MANIFESTS.campAdjacent];
    return MANIFESTS.provinces.find((p) => p.id === leader)?.adjacent ?? [];
}
/** VP for occupying `province` now: its value minus 3 per opposing legionnaire, never below 0. */
export function occupationVp(state, actor, province) {
    const value = MANIFESTS.provinces.find((p) => p.id === province)?.vp ?? 0;
    const opposing = (state.board.provinces[province]?.legionnaires ?? []).filter((id) => id !== actor).length;
    return Math.max(0, value - MANIFESTS.scoring.provinceOpponentPenalty * opposing);
}
export function militaryOptions(state, player) {
    const out = [];
    if (player.supply > 0)
        out.push({ option: 'deploy' });
    for (const province of leaderDestinations(player.leader))
        out.push({ option: 'move', province });
    if (player.leader !== 'camp' &&
        player.legionnaires > 0 &&
        !(state.board.provinces[player.leader]?.legionnaires ?? []).includes(player.id)) {
        out.push({ option: 'occupy' });
    }
    return out;
}
export function military(state, actor, payload) {
    const p = payload;
    const player = playerOf(state, actor);
    const events = [];
    switch (p?.option) {
        case 'deploy': {
            if (player.supply < 1)
                return fail('ILLEGAL', 'no tokens left in the supply');
            const next = updatePlayer(state, actor, (pl) => ({
                ...pl,
                supply: pl.supply - 1,
                legionnaires: pl.legionnaires + 1,
            }));
            events.push({ type: 'TokensDeployed', player: actor, camp: 'legionnaires', count: 1 });
            return succeed(afterAction(next), events);
        }
        case 'move': {
            const to = p.province;
            if (typeof to !== 'string' || !leaderDestinations(player.leader).includes(to)) {
                return fail('ILLEGAL', `the leader cannot move from ${player.leader} to ${String(to)}`);
            }
            const province = state.board.provinces[to];
            if (!province)
                return fail('ILLEGAL', `no province ${to}`);
            const seized = province.tile;
            let next = updatePlayer(state, actor, (pl) => ({
                ...pl,
                leader: to,
                forumTiles: seized ? [...pl.forumTiles, seized] : pl.forumTiles,
            }));
            if (seized) {
                next = {
                    ...next,
                    board: {
                        ...next.board,
                        provinces: { ...next.board.provinces, [to]: { ...province, tile: null } },
                    },
                };
            }
            events.push({
                type: 'LeaderMoved',
                player: actor,
                to,
                seizedTileId: seized ? seized.id : null,
            });
            return succeed(afterAction(next), events);
        }
        case 'occupy': {
            if (player.leader === 'camp')
                return fail('ILLEGAL', 'the leader is still in the camp');
            if (player.legionnaires < 1)
                return fail('ILLEGAL', 'no legionnaire in the camp');
            const id = player.leader;
            const province = state.board.provinces[id];
            if (!province)
                return fail('ILLEGAL', `no province ${id}`);
            if (province.legionnaires.includes(actor))
                return fail('ILLEGAL', `already have a legionnaire in ${id}`);
            const vp = occupationVp(state, actor, id);
            let next = updatePlayer(state, actor, (pl) => ({ ...pl, legionnaires: pl.legionnaires - 1 }));
            next = {
                ...next,
                board: {
                    ...next.board,
                    provinces: {
                        ...next.board.provinces,
                        [id]: { ...province, legionnaires: [...province.legionnaires, actor] },
                    },
                },
            };
            next = addVp(next, actor, vp);
            events.push({ type: 'ProvinceOccupied', player: actor, province: id, vp });
            return succeed(afterAction(next), events);
        }
        default:
            return fail('ILLEGAL', "Military action needs { option: 'deploy' | 'move' | 'occupy' }");
    }
}
//# sourceMappingURL=military.js.map